import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { AutocompleteAPI, FieldAPI, FieldListAPI, FieldListValue, FormAPI, PanelAPI, WizardAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClick, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ActionPlanNewAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface Action {
  dsAction: string,
  nmResponsible: string,
  detailing: string,
  nrSeqProvider: number,
}

@Controller({ fragment: FragmentNames.ACTION_PLAN_NEW })
class ActionPlanNewController extends BaseController {

  @Inject(Aliases.ACTIONS_LIST_FIELD)
  private actionsList: FieldListAPI;
  @Inject(Aliases.FORM_ACTION)
  private formAction: FormAPI;
  @Inject(Aliases.FORM_PLAN_DATA)
  private formPlanData: FormAPI;
  @Inject(Aliases.FIELD_ACTION)
  private fieldAction: FieldAPI;
  @Inject(Aliases.FIELD_RESPONSIBLE)
  private fieldResponsible: FieldAPI;
  @Inject(Aliases.FIELD_DETAILS)
  private fieldDetails: FieldAPI;
  @Inject(Aliases.WIZARD_ACTION_PLAN)
  private wizardActionPlan: WizardAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.AUTOCOMPLETE_PROVIDER)
  private autcompleteProvider: AutocompleteAPI;

  @OnChange(Aliases.ACTIONS_LIST_FIELD)
  onChangeActionsList(changeEvent: ChangeEvent<FieldListValue>): void {
    this.actionsList.setValue(changeEvent.value);
  }

  @OnMount(Aliases.ACTIONS_LIST_FIELD)
  onMountList(): void {
    this.actionsList.setValue([]);
  }

  @OnClick(Aliases.LINK_ADD)
  onSaveActionClick(): void {
    const record = this.formAction.getRecord() as unknown as Action;
    if (this.formAction.isValid()) {
      if (!isEmptyArrayOrObject(record)) {
        const value = [...this.actionsList.getValue()];
        const specialty = {
          nmResponsible: record.nmResponsible,
          dsAction: record.dsAction,
          detailing: record.detailing,
        };
        value.push(specialty);
        const reversed = [...value].reverse();
        this.actionsList.setValue(reversed);
        this.formAction.setValue({});
        this.fieldAction.setState({ showValidations: false });
        this.fieldDetails.setState({ showValidations: false });
        this.fieldResponsible.setState({ showValidations: false });
      }
    }
  }

  @OnValidate(Aliases.FORM_PLAN_DATA)
  onValidatePersonalInformationForm(event: ValidateEvent): void {
    const { valid } = event;
    this.wizardActionPlan.setStepDone('step-action-plan-data', valid);
  }

  @OnValidate(Aliases.FORM_ACTION)
  onValidateAccountDataForm(): void {
    const value = [...this.actionsList.getValue()];
    if (this.wizardActionPlan.getCurrentStepIdentifier() == 'step-actions') {
      this.wizardActionPlan.setStepDone('step-actions', value.length > 0);
    }
  }

  @OnSubmit(Aliases.WIZARD_ACTION_PLAN)
  onSubmitWizard(): void {
    const actions = [...this.actionsList.getValue()];
    const formActionRecord = this.formAction.getRecord() as unknown as Action;
    const { nrSeqProvider } = this.formPlanData.getRecord() as unknown as Action;

    if (!isEmptyArrayOrObject(formActionRecord)) {
      const message = MessageService.translate('provider.action-not-inserted').replace('#@DS_ACAO#@', formActionRecord.dsAction);
      ToastService.open({ message, level: ToastLevel.ERROR });
      return;
    }

    if (actions.length == 0) {
      ToastService.open({ message: MessageService.translate('provider.required-inform-action'), level: ToastLevel.ERROR });
      return;
    }

    const body = { ...this.formPlanData.getRecord(), actions };
    this.panelLoader.setVisible(true);
    this.actionsList.setReadOnly(true);
    HttpService.post<Record<string, Value>>(Endpoint.POST.NEW_ACTION_PLAN, { nrSeqProvider }, { body })
      .then((message) => {
        this.panelLoader.setVisible(false);
        this.wizardActionPlan.setReadOnly(true);
        this.formAction.setEditing(false);
        this.formPlanData.setEditing(false);
        this.autcompleteProvider.setReadOnly(true);
        ToastService.open(message);
      }).catch((error) => {
        this.panelLoader.setVisible(false);
        this.actionsList.setReadOnly(false);
        ToastService.open(error);
      });
  }

}

export {
  ActionPlanNewController,
};
